import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { AppNavbarVisibilityProvider } from './contexts/AppNavbarVisibilityContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { Navbar } from './components/Navbar';
import { BottomNav } from './components/BottomNav';
import { Home } from './pages/Home';
import { Auth } from './pages/Auth';
import { PersonalTrainers } from './pages/PersonalTrainers';
import { WorkoutPlans } from './pages/WorkoutPlans';
import { MemberDashboard } from './pages/MemberDashboard';
import { TrainerDashboard } from './pages/TrainerDashboard';
import { Account } from './pages/Account';
import { AccountProfilePage } from './pages/AccountProfilePage';
import { AccountSettingsPage } from './pages/account/AccountSettingsPage';
import { Exercises } from './pages/Exercises';
import { ExerciseDetailPage } from './pages/ExerciseDetailPage';
import { WorkoutSession } from './pages/WorkoutSession';
import { ActiveWorkoutPlans } from './pages/ActiveWorkoutPlans';
import { WorkoutDetailsPage } from './pages/WorkoutDetailsPage';
import { Notifications } from './components/Notifications';
import { MemberDetailPage } from './pages/MemberDetailPage';
import { WorkoutPlanDetailPage } from './pages/WorkoutPlanDetailPage';
import { TrainerWorkoutDetailPage } from './pages/TrainerWorkoutDetailPage';
import { TrainerWorkoutCreatePage } from './pages/TrainerWorkoutCreatePage';
import { Chat } from './pages/Chat';
import { SprintBoard } from './pages/SprintBoard';
import { NotFound } from './pages/NotFound';
import { TermsPage } from './pages/TermsPage';
import { PrivacyPage } from './pages/PrivacyPage';
import { InviteLandingPage } from './pages/InviteLandingPage';
import { LoadingPreviewPage } from './pages/LoadingPreviewPage';
import { useRoutePersistence } from './hooks/useRoutePersistence';
import { useTrainerPageVisitLogging } from './hooks/useTrainerPageVisitLogging';
import { PwaInstallPrompt } from './components/PwaInstallPrompt';

/** Orphan calendar URL → member detail (PRD FR4). */
function MemberCalendarRedirect() {
  const { memberId } = useParams<{ memberId: string }>();
  return <Navigate to={`/trainer-dashboard/member/${memberId}`} replace />;
}

function AppRoutes() {
  // Enable route persistence
  useRoutePersistence();
  useTrainerPageVisitLogging();

  return (
    <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/invite/:code" element={<InviteLandingPage />} />
            <Route path="/personal-trainers" element={<PersonalTrainers />} />
            <Route path="/exercises" element={<Exercises />} />
            <Route path="/exercise/:id" element={<ExerciseDetailPage />} />
            <Route path="/workout-plans" element={<WorkoutPlans />} />
            <Route 
              path="/member-dashboard" 
              element={
                <ProtectedRoute requiredRole="member">
                  <MemberDashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/trainer-dashboard" 
              element={
                <ProtectedRoute requiredRole="trainer">
                  <TrainerDashboard />
                </ProtectedRoute>
              } 
            />
            <Route
              path="/trainer-dashboard/member/:memberId/calendar"
              element={
                <ProtectedRoute requiredRole="trainer">
                  <MemberCalendarRedirect />
                </ProtectedRoute>
              }
            />
            <Route 
              path="/trainer-dashboard/member/:memberId/workout/:workoutId" 
              element={
                <ProtectedRoute requiredRole="trainer">
                  <TrainerWorkoutDetailPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/trainer-dashboard/member/:memberId" 
              element={
                <ProtectedRoute requiredRole="trainer">
                  <MemberDetailPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/trainer-dashboard/workout-plan/new" 
              element={
                <ProtectedRoute requiredRole="trainer">
                  <WorkoutPlanDetailPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/trainer-dashboard/workout-plan/:planId/workout/:workoutId" 
              element={
                <ProtectedRoute requiredRole="trainer">
                  <TrainerWorkoutDetailPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/trainer-dashboard/workout-plan/:id" 
              element={
                <ProtectedRoute requiredRole="trainer">
                  <WorkoutPlanDetailPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/trainer-dashboard/workout/new" 
              element={
                <ProtectedRoute requiredRole="trainer">
                  <TrainerWorkoutCreatePage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/trainer-dashboard/workout/:id" 
              element={
                <ProtectedRoute requiredRole="trainer">
                  <TrainerWorkoutDetailPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/active-workout-plans" 
              element={
                <ProtectedRoute requiredRole="member">
                  <ActiveWorkoutPlans />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/workout-session/:id" 
              element={
                <ProtectedRoute requiredRole="member">
                  <WorkoutSession />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/workout-details/:param" 
              element={
                <ProtectedRoute requiredRole="member">
                  <WorkoutDetailsPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/notifications" 
              element={
                <ProtectedRoute requiredRole="member">
                  <Notifications />
                </ProtectedRoute>
              }
            />
            <Route 
              path="/chat" 
              element={
                <ProtectedRoute>
                  <Chat />
                </ProtectedRoute>
              }
            />
            <Route
              path="/account/profile"
              element={
                <ProtectedRoute requiredRole="member">
                  <AccountProfilePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/account/:settingsKey"
              element={
                <ProtectedRoute requiredRole="member">
                  <AccountSettingsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/account"
              element={
                <ProtectedRoute requiredRole="member">
                  <Account />
                </ProtectedRoute>
              }
            />
            <Route path="/sprint-board" element={<SprintBoard />} />
            <Route path="/loading-preview" element={<LoadingPreviewPage />} />
            {/* Catch-all route for 404 */}
            <Route path="*" element={<NotFound />} />
          </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <AppNavbarVisibilityProvider>
          <div className="App">
            <Navbar />
            <AppRoutes />
            <PwaInstallPrompt />
            <BottomNav />
          </div>
        </AppNavbarVisibilityProvider>
      </Router>
    </AuthProvider>
  );
}

export default App; 